<template>
  <client-only>
    <TransitionRoot :show="isOpen" as="template">
      <Dialog
        component-name="modal-bath-concept-details"
        data-skin="white"
        class="relative z-50"
        data-lenis-prevent
      >
        <TransitionChild
          enter="transition-opacity duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <!-- The backdrop, rendered as a fixed sibling to the panel container -->
          <div
            class="modal-backdrop"
            aria-hidden="true"
            @click="emit('close')"
          ></div>
        </TransitionChild>

        <!-- Full-screen scrollable container -->
        <div
          class="pointer-events-none overflow-clip fixed inset-0 flex justify-start w-full h-full"
        >
          <!-- Container to center the panel -->
          <div
            class="flex justify-start items-stretch w-full h-full max-h-full"
          >
            <TransitionChild
              enter="transition-transform duration-300 ease-out delay-200"
              enter-from="-translate-x-full"
              enter-to="translate-x-0"
              leave="transition-transform duration-300 ease-in"
              leave-from="translate-x-0"
              leave-to="-translate-x-full"
            >
              <!-- The actual dialog panel -->
              <component
                :is="$device.isMobile ? 'div' : DialogPanel"
                data-skin="white"
                class="pointer-events-auto flex flex-col justify-between w-[calc(100vw-60px)] max-w-[650px] h-full max-h-full bg-skin-base"
              >
                <div
                  class="flex flex-col gap-2xl grow overflow-y-auto w-full px-edge pt-edge"
                >
                  <div
                    v-if="detailsData"
                    class="flex flex-col justify-between gap-3xl min-h-full"
                  >
                    <div class="flex flex-col gap-2xl">
                      <header class="flex flex-col gap-2xl">
                        <h1 class="text-heading-4">{{ detailsData.name }}</h1>

                        <div class="flex flex-col gap-xl">
                          <div class="w-full">
                            <div
                              v-if="
                                detailsData.designer || detailsData.installation
                              "
                              class="flex items-baseline justify-start h-10 max-w-full overflow-x-auto gap-2xl"
                            >
                              <btn-tab
                                :title="$t.labels.description"
                                @click="currentTab = 'description'"
                                :pressed="currentTab === 'description'"
                                class="h-full"
                              />

                              <btn-tab
                                v-if="detailsData.designer"
                                :title="$t.labels.advice_designer"
                                @click="currentTab = 'designer'"
                                :pressed="currentTab === 'designer'"
                                class="h-full"
                              />

                              <btn-tab
                                v-if="detailsData.installation"
                                :title="$t.labels.advice_installation"
                                @click="currentTab = 'installation'"
                                :pressed="currentTab === 'installation'"
                                class="h-full"
                              />
                            </div>
                            <divider />
                          </div>

                          <transition-fade duration="quick">
                            <div
                              v-if="
                                detailsData.designer &&
                                currentTab === 'designer'
                              "
                              key="designer"
                              class="text-body"
                              v-html="detailsData.designer"
                            ></div>

                            <div
                              v-if="
                                detailsData.installation &&
                                currentTab === 'installation'
                              "
                              key="installation"
                              class="text-body"
                              v-html="detailsData.installation"
                            ></div>

                            <div
                              v-else
                              key="description"
                              class="text-body"
                              v-html="detailsData.description"
                            ></div>
                          </transition-fade>
                        </div>
                      </header>

                      <!-- end WALLCOVERINGS -->
                      <collapse-group :always-open="false" class="w-full">
                        <collapse
                          :title="$t.labels.wallcoverings"
                          class="w-full"
                        >
                          <template #header="collapseProps">
                            <btn-solid
                              size="big"
                              button-tag="summary"
                              :title="$t.labels.wallcoverings"
                              :icon-right="
                                collapseProps.isOpen
                                  ? collapseProps.icons.opened
                                  : collapseProps.icons.closed
                              "
                              class="w-full"
                            />
                          </template>

                          <div class="flex flex-col gap-2xl pb-xl w-full">
                            <!-- WALLCOVERING PRODUCTS -->
                            <div
                              v-for="item in detailsData.wallcoverings.products"
                              class="flex flex-col gap-md w-full"
                            >
                              <btn
                                class="flex gap-md items-center pointer-events-none"
                                :title="item.name"
                                :action="{
                                  href: `/${$route.params.country}/${$route.params.locale}/products/${item.slug}`,
                                  target: '_blank',
                                }"
                              >
                                <div
                                  class="w-16 min-w-16 phablet:w-24 phablet:min-w-24 tablet:w-30 tablet:min-w-30 bg-skin-muted pointer-events-auto overflow-hidden"
                                >
                                  <media
                                    tag="div"
                                    v-bind="{ ...item.asset }"
                                    :sizes="{
                                      base: 100,
                                      phablet: 150,
                                      tablet: 150,
                                    }"
                                    class="w-full h-full transition-transform duration-1000 group-hover/btn:scale-105"
                                  />
                                </div>
                                <div
                                  class="text-body-big pointer-events-auto"
                                  v-html="item.name"
                                ></div>
                              </btn>

                              <div class="flex flex-col w-full gap-lg">
                                <div class="flex flex-col gap-sm w-full">
                                  <header
                                    class="text-body"
                                    v-html="$t.labels.stucco"
                                  ></header>
                                  <div
                                    v-for="st in item.stucco"
                                    class="flex gap-lg items-center"
                                  >
                                    <div
                                      class="w-16 phablet:w-24 tablet:w-30 h-5"
                                      :style="{ backgroundColor: st.hex }"
                                    ></div>
                                    <div
                                      class="relative top-0.5 text-body-small truncate grow"
                                      v-html="st.name"
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- end WALLCOVERING PRODUCTS -->

                            <!-- PAINTS -->
                            <div
                              v-if="detailsData.wallcoverings.paint?.length"
                              class="contents"
                            >
                              <hr class="w-full opacity-40" />

                              <div class="flex flex-col w-full gap-sm">
                                <header
                                  class="text-body/none"
                                  v-html="$t.labels.paint"
                                ></header>

                                <div
                                  v-for="item in detailsData.wallcoverings
                                    .paint"
                                  class="flex gap-lg items-center"
                                >
                                  <div
                                    class="w-16 phablet:w-24 tablet:w-30 h-5"
                                    :style="{ backgroundColor: item.hex }"
                                  ></div>
                                  <div
                                    class="relative top-0.5 text-body-small truncate grow"
                                    v-html="item.name"
                                  ></div>
                                </div>
                              </div>
                            </div>
                            <!-- end PAINTS -->
                          </div>
                        </collapse>
                        <!-- end WALLCOVERINGS -->

                        <!-- FLOORS -->
                        <collapse :title="$t.labels.floor" class="w-full">
                          <template #header="collapseProps">
                            <btn-solid
                              size="big"
                              button-tag="summary"
                              :title="$t.labels.floor"
                              :icon-right="
                                collapseProps.isOpen
                                  ? collapseProps.icons.opened
                                  : collapseProps.icons.closed
                              "
                              class="w-full"
                            />
                          </template>

                          <div class="flex flex-col gap-3xl pb-xl w-full">
                            <div
                              v-for="item in detailsData.floors"
                              class="flex flex-col gap-md w-full"
                            >
                              <btn
                                class="flex gap-md items-center pointer-events-none"
                                :title="item.name"
                                :action="{
                                  href: `/${$route.params.country}/${$route.params.locale}/products/${item.slug}`,
                                  target: '_blank',
                                }"
                              >
                                <div
                                  class="w-16 min-w-16 phablet:w-24 phablet:min-w-24 tablet:w-30 tablet:min-w-30 bg-skin-muted pointer-events-auto overflow-hidden"
                                >
                                  <media
                                    tag="div"
                                    v-bind="{ ...item.asset }"
                                    :sizes="{
                                      base: 100,
                                      phablet: 150,
                                      tablet: 150,
                                    }"
                                    class="w-full h-full transition-transform duration-1000 group-hover/btn:scale-105"
                                  />
                                </div>
                                <div
                                  class="text-body-big pointer-events-auto"
                                  v-html="item.name"
                                ></div>
                              </btn>

                              <div class="flex flex-col w-full gap-lg">
                                <div class="flex flex-col gap-sm w-full">
                                  <header
                                    class="text-body"
                                    v-html="$t.labels.stucco"
                                  ></header>
                                  <div
                                    v-for="st in item.stucco"
                                    class="flex gap-lg items-center"
                                  >
                                    <div
                                      class="w-16 phablet:w-24 tablet:w-30 h-5"
                                      :style="{ backgroundColor: st.hex }"
                                    ></div>
                                    <div
                                      class="relative top-0.5 text-body-small truncate grow"
                                      v-html="st.name"
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </collapse>
                        <!-- end FLOORS -->

                        <!-- FURNITURES -->
                        <collapse :title="'Arredo bagno'" class="w-full">
                          <template #header="collapseProps">
                            <btn-solid
                              size="big"
                              button-tag="summary"
                              :title="'Arredo bagno'"
                              :icon-right="
                                collapseProps.isOpen
                                  ? collapseProps.icons.opened
                                  : collapseProps.icons.closed
                              "
                              class="w-full"
                            />
                          </template>

                          <div class="flex flex-col gap-3xl pb-xl w-full">
                            <div
                              v-for="item in detailsData.furnitures"
                              class="flex gap-md items-center pointer-events-none"
                            >
                              <div
                                class="w-16 min-w-16 phablet:w-24 phablet:min-w-24 tablet:w-30 tablet:min-w-30 bg-skin-muted pointer-events-auto overflow-hidden"
                              >
                                <media
                                  tag="div"
                                  v-bind="{ ...item.asset }"
                                  :sizes="{
                                    base: 100,
                                    phablet: 150,
                                    tablet: 150,
                                  }"
                                  class="w-full h-full transition-transform duration-1000 group-hover/btn:scale-105"
                                />
                              </div>
                              <div class="flex flex-col gap-2xs grow">
                                <div
                                  class="text-body-big pointer-events-auto"
                                  v-html="item.name"
                                ></div>
                                <div
                                  class="text-body-small pointer-events-auto"
                                  v-html="item.description"
                                ></div>
                              </div>
                            </div>
                          </div>
                        </collapse>
                        <!-- end FURNITURES -->
                      </collapse-group>
                    </div>

                    <footer class="w-full pb-edge">
                      <btn-list
                        class="w-full"
                        size="small"
                        :items="[
                          {
                            icon: 'download',
                            title: $t.labels.download_imgs_and_data_sheet,
                            action: detailsData.download.action,
                          },
                          {
                            icon: 'info',
                            title: $t.labels.info_request,
                            action: { to: '/support' },
                          },
                        ]"
                      />
                    </footer>
                  </div>

                  <div
                    v-else
                    class="flex justify-center items-center w-full h-full"
                  >
                    <spinner :spinning="true" />
                  </div>
                </div>
              </component>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </client-only>
</template>

<script setup>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: false,
    default: false,
  },
  slug: {
    type: String,
    required: true,
  },
});

const route = useRoute();
const emit = defineEmits(["close"]);

const { data } = await useFetch(
  `/api/bathconcept_project_details?slug=${route.params.id}&lng=${route.params.locale}`
);

const currentTab = ref("description");
const detailsData = ref();
detailsData.value = { ...data.value };

watch(
  () => route.name,
  () => {
    emit("close");
  }
);

watch(
  () => route.params.id,
  (value) => {
    if (!value) return;

    $fetch(
      `/api/bathconcept_project_details?slug=${value}&lng=${route.params.locale}`
    ).then((res) => {
      detailsData.value = { ...res };
    });
  }
);
</script>
